// @flow
import React, {
  useRef,
  useEffect,
  useState,
} from 'react';
import Select from '@atlaskit/select';
import {
  CheckboxOption,
} from '@atlaskit/select/dist/cjs/components/input-options';

import type {
  ElementRef,
} from 'react';
import type {
  FilterOption,
  FilterOptions,
  FilterType,
} from 'chronos-types';

import {
  SelectAsyncPaginate,
} from 'chronos-components';
import {
  capitalizeFirstLetter as cfl,
} from 'chronos-utils';

import * as S from './styled';


type Props = {
  /**
   * Selected filter type
   * */
  selectedType: FilterType,
  /**
   * Already selected filters.
   * */
  selectedFilters: Filters,
  /**
   * Type options
   * */
  typeOptions: FilterOptions,
  /**
   * Function that is called whenever the filter option has been added
   * */
  onAdd: { ({ selectedType: FilterType, option: FilterOption }): void },
  /**
   * Function that is called whenever the filter option has been removed
   * */
  onRemove: { ({ selectedType: FilterType, option: FilterOption }): void },
  /**
   * Function that is called whenever the filter type of the changes.
   * */
  onTypeChange: { (value: FilterType): void },
  /**
   * Function to load options
   * */
  loadOptions: { (search: string): void },
};

const FilterSelect = ({
  selectedType,
  typeOptions,
  selectedFilters,
  loadOptions,
  onAdd,
  onRemove,
  onTypeChange,
}: Props) => {
  const typeSelectRef  = useRef(null);
  const valueSelectRef = useRef(null);
  const [isInitialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      setTimeout(() => {
        if (typeSelectRef?.current) {
          typeSelectRef.current.focus();
        }
      }, 400);
      setInitialized(true);
    }
  });

  return (
    <S.FilterSelect>
      <S.TypeSelectContent>
        <Select
          name={`filterCategory_${selectedType}`}
          placeholder="Select filter type"
          classNamePrefix="chronosPopupSelect"
          openMenuOnFocus
          value={{
            label: cfl(selectedType),
            value: selectedType,
          }}
          options={typeOptions}
          styles={{
            control: provided => ({
              ...provided,
              borderBottomRightRadius: 0,
              borderTopRightRadius: 0,
            }),
          }}
          onChange={(filterType) => {
            if (valueSelectRef?.current) {
              valueSelectRef.current.focus();
            }
            if (onTypeChange) {
              onTypeChange(filterType);
            }
          }}
          menuPortalTarget={document.body}
          ref={typeSelectRef}
        />
      </S.TypeSelectContent>

      <S.ValueSelectContent>
        <SelectAsyncPaginate
          name="filterValue"
          placeholder="Select filter value"
          classNamePrefix="chronosPopupSelect"
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          controlShouldRenderValue={false}
          components={{
            Option: CheckboxOption,
          }}
          openMenuOnFocus
          isMulti
          isClearable={false}
          isDisabled={!selectedType}
          cacheUniq={selectedType}
          cacheOptions={selectedType}
          styles={{
            noOptionsMessage: styles => ({
              ...styles,
              height: '300px',
            }),
            control: provided => ({
              ...provided,
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: 0,
            }),
          }}
          selectedOptions={(
            selectedFilters.filter(f => (
              f.selectedType.value === selectedType
            ))
          )}
          isOptionSelected={option => (
            option.isSelected
          )}
          loadOptions={loadOptions}
          onChange={(
            options,
            {
              action,
              option,
            },
          ) => {
            if (
              (
                !option.meta
                || option.meta.type !== 'title'
              )
              && selectedType
            ) {
              if (action === 'select-option') {
                onAdd({
                  selectedType,
                  option,
                });
              } else {
                onRemove({
                  selectedType,
                  option,
                });
              }
            }
          }}
          selectRef={valueSelectRef}
          menuPortalTarget={document.body}
        />
      </S.ValueSelectContent>
    </S.FilterSelect>
  );
};

export default FilterSelect;
